const DEFAULT_NB_ALBUM = 20;

/** Deezer public artist image endpoint (redirects to CDN artwork). */
export const deezerArtistImageUrl = deezerId =>
  deezerId ? `https://api.deezer.com/artist/${deezerId}/image` : "";

function spotifyImages(imageBig, imageSmall) {
  const big = imageBig || "";
  const small = imageSmall || big;
  if (!big) {
    return [];
  }
  return [{ url: big }, { url: small }, { url: small }];
}

function deezerPicturesFromUrl(url) {
  if (!url) {
    return {
      picture_small: null,
      picture_medium: null,
      picture_big: null,
      picture_xl: null,
    };
  }
  return {
    picture_small: url,
    picture_medium: url,
    picture_big: url,
    picture_xl: url,
  };
}

const firstUrl = (...urls) =>
  urls.find(url => url && String(url).trim()) || "";

function spotifyImageUrls(row) {
  const fromDetails = (row.spotify_details?.images || []).filter(
    img => img && img.url,
  );
  const big = fromDetails[0]?.url || row.image_big || "";
  const small =
    (fromDetails[2] || fromDetails[1] || fromDetails[0])?.url ||
    row.image_small ||
    big;
  return { big, small };
}

function deezerImageUrls(row) {
  const d = row.deezer_details;
  const big =
    d?.picture_big ||
    (row.deezer_identifier
      ? deezerArtistImageUrl(row.deezer_identifier)
      : "") ||
    "";
  const small = d?.picture_small || big;
  return { big, small };
}

function amazonImageUrls(row) {
  const images = (row.amazon_music_details?.data?.artist?.images || []).filter(
    img => img && img.url,
  );
  const big = images[0]?.url || "";
  const small =
    images.length === 1
      ? big
      : images[images.length - 1]?.url || big;
  return { big, small };
}

/** Default artist avatar: Spotify, then Deezer, then Amazon Music. */
export function resolveArtistDefaultImages(row = {}) {
  const spotify = spotifyImageUrls(row);
  const deezer = deezerImageUrls(row);
  const amazon = amazonImageUrls(row);

  const image_big = firstUrl(spotify.big, deezer.big, amazon.big);
  const image_small = firstUrl(
    spotify.small,
    deezer.small,
    amazon.small,
    image_big,
  );

  return { image_big, image_small };
}

function hasDeezerPictures(details) {
  return Boolean(details?.picture_small || details?.picture_big);
}

function hasAmazonPictures(details) {
  const images = details?.data?.artist?.images;
  return Array.isArray(images) && images.some(img => img && img.url);
}

function hasSpotifyPictures(details) {
  return Boolean(
    (details?.images || []).some(img => img && img.url) || details?.id,
  );
}

/** Build spotify_details from a store search / dropdown selection. */
export function buildSpotifyDetailsFromSelection(artist, fallbackName = "") {
  if (!artist?.value) {
    return null;
  }
  const name = artist.label || artist.name || fallbackName;
  const imageBig = artist.image_big || "";
  const imageSmall = artist.image_small || imageBig;
  return {
    id: artist.value,
    name,
    images: spotifyImages(imageBig, imageSmall),
    popularity: Number(artist.popularity) || 0,
    followers: { total: Number(artist.followers) || 0 },
  };
}

/** Build deezer_details from a store search / dropdown selection. */
export function buildDeezerDetailsFromSelection(artist, fallbackName = "") {
  if (artist?.value == null || artist?.value === "") {
    return null;
  }
  const name = artist.label || artist.name || fallbackName;
  const pics = deezerPicturesFromUrl(
    artist.picture_small ||
      artist.image_small ||
      artist.picture_big ||
      artist.image_big ||
      null,
  );
  return {
    id: Number(artist.value),
    name,
    nb_album: Number(artist.nb_album) || DEFAULT_NB_ALBUM,
    nb_fan: Number(artist.nb_fan) || 0,
    ...pics,
  };
}

/** Build amazon_music_details from a store search / dropdown selection. */
export function buildAmazonDetailsFromSelection(artist, fallbackName = "") {
  if (!artist?.value) {
    return null;
  }
  const name = artist.label || artist.name || fallbackName;
  const img = artist.image_big || artist.image_small || null;
  return {
    data: {
      artist: {
        id: artist.value,
        name,
        images: img
          ? [{ url: img, imageType: "PROFILE", height: 500, width: 500 }]
          : [],
      },
    },
  };
}

function buildSpotifyDetailsFromRow(row) {
  if (!row.spotify_identifier) {
    return null;
  }
  const name = row.name || "";
  const imageBig = row.image_big || "";
  const imageSmall = row.image_small || imageBig;
  return {
    id: row.spotify_identifier,
    name,
    images: spotifyImages(imageBig, imageSmall),
    popularity: Number(row.popularity) || 0,
    followers: { total: Number(row.followers) || 0 },
  };
}

function buildDeezerDetailsFromRow(row) {
  if (!row.deezer_identifier) {
    return null;
  }
  const pics = deezerPicturesFromUrl(
    deezerArtistImageUrl(row.deezer_identifier),
  );
  return {
    id: Number(row.deezer_identifier),
    name: row.name || "",
    nb_album: DEFAULT_NB_ALBUM,
    nb_fan: Math.round(Number(row.followers) * 0.75) || 0,
    ...pics,
  };
}

function buildAmazonDetailsFromRow(row) {
  if (!row.amazon_music_identifier) {
    return null;
  }
  return {
    data: {
      artist: {
        id: row.amazon_music_identifier,
        name: row.name || "",
        images: [],
      },
    },
  };
}

/**
 * Normalize an artist row: keep stored spotify_details / deezer_details /
 * amazon_music_details, or synthesize minimal blobs from identifiers.
 * Store details are set when a store artist is matched (see buildArtistSavePayload).
 */
export function enrichArtistRow(row) {
  if (!row) {
    return row;
  }

  const spotify_details = hasSpotifyPictures(row.spotify_details)
    ? row.spotify_details
    : buildSpotifyDetailsFromRow(row);

  const deezer_details =
    row.deezer_details === null
      ? null
      : hasDeezerPictures(row.deezer_details)
        ? row.deezer_details
        : buildDeezerDetailsFromRow(row);

  const amazon_music_details =
    row.amazon_music_details === null
      ? null
      : hasAmazonPictures(row.amazon_music_details)
        ? row.amazon_music_details
        : buildAmazonDetailsFromRow(row);

  const withDetails = {
    ...row,
    ...(spotify_details ? { spotify_details } : {}),
    ...(row.deezer_details === null
      ? { deezer_details: null }
      : deezer_details
        ? { deezer_details }
        : {}),
    ...(row.amazon_music_details === null
      ? { amazon_music_details: null }
      : amazon_music_details
        ? { amazon_music_details }
        : {}),
  };

  return {
    ...withDetails,
    ...resolveArtistDefaultImages(withDetails),
  };
}

/** Merge matched store selections into an artist row (identifiers + detail blobs). */
export function applyMatchedStoreArtists(
  row,
  { spotifyArtist, deezerArtist, amazonArtist, name = row?.name } = {},
) {
  const spotifyId = spotifyArtist?.value ?? row?.spotify_identifier ?? "";
  const deezerId =
    deezerArtist?.value != null && deezerArtist?.value !== ""
      ? String(deezerArtist.value)
      : row?.deezer_identifier ?? "";
  const amazonId = amazonArtist?.value ?? row?.amazon_music_identifier ?? "";

  return enrichArtistRow({
    ...row,
    name: name || row?.name || "",
    spotify_identifier: spotifyId,
    deezer_identifier: deezerId,
    amazon_music_identifier: amazonId,
    spotify_details: spotifyArtist
      ? buildSpotifyDetailsFromSelection(spotifyArtist, name)
      : row?.spotify_details ?? null,
    deezer_details: deezerArtist
      ? buildDeezerDetailsFromSelection(deezerArtist, name)
      : row?.deezer_details ?? null,
    amazon_music_details: amazonArtist
      ? buildAmazonDetailsFromSelection(amazonArtist, name)
      : row?.amazon_music_details ?? null,
    followers:
      Number(spotifyArtist?.followers) ||
      Number(row?.followers) ||
      0,
    popularity:
      Number(spotifyArtist?.popularity) ||
      Number(row?.popularity) ||
      0,
  });
}

/** Payload for POST/PATCH artists/ including store detail blobs for demo persistence. */
export function buildArtistSavePayload({
  name,
  email,
  spotifyId,
  deezerId,
  amazonId,
  appleId,
  imageBig,
  imageSmall,
  followers,
  popularity,
  spotifyArtist,
  deezerArtist,
  amazonArtist,
  sub_user_id,
  owner,
  can_be_deleted,
  id,
}) {
  const base = {
    ...(id != null ? { id } : {}),
    name,
    email,
    spotify_identifier: spotifyId || spotifyArtist?.value || "",
    deezer_identifier:
      deezerId != null && deezerId !== ""
        ? String(deezerId)
        : deezerArtist?.value != null
          ? String(deezerArtist.value)
          : "",
    amazon_music_identifier: amazonId || amazonArtist?.value || "",
    apple_identifier: appleId || "",
    image_big: imageBig || "",
    image_small: imageSmall || "",
    followers: Number(followers) || Number(spotifyArtist?.followers) || 0,
    popularity: Number(popularity) || Number(spotifyArtist?.popularity) || 0,
    ...(owner != null ? { owner } : {}),
    ...(can_be_deleted != null ? { can_be_deleted } : {}),
    ...(sub_user_id != null ? { sub_user_id } : {}),
  };

  return applyMatchedStoreArtists(base, {
    spotifyArtist,
    deezerArtist,
    amazonArtist,
    name,
  });
}

/** Map stored spotify_details to react-select option (Spotify artwork only). */
export function spotifyDetailsToSelectOption(details, row = {}) {
  if (!details?.id) {
    return null;
  }
  const images = (details.images || []).filter(img => img && img.url);
  const image_big = images[0]?.url || row.image_big || "";
  const image_small = (images[2] || images[1] || images[0])?.url || image_big;
  return {
    value: details.id,
    label: details.name,
    name: details.name,
    popularity: details.popularity ?? 0,
    followers: details.followers?.total ?? 0,
    image_big,
    image_small,
  };
}

/** Map stored deezer_details to react-select option (Deezer artwork only). */
export function deezerDetailsToSelectOption(details) {
  if (!details?.id) {
    return null;
  }
  return {
    value: details.id,
    label: details.name,
    name: details.name,
    nb_album: details.nb_album,
    nb_fan: details.nb_fan,
    image_small: details.picture_small,
    image_medium: details.picture_medium,
    image_big: details.picture_big,
    image_xl: details.picture_xl,
    picture_small: details.picture_small,
    picture_medium: details.picture_medium,
    picture_big: details.picture_big,
    picture_xl: details.picture_xl,
  };
}

/** Map stored amazon_music_details to react-select option (Amazon artwork only). */
export function amazonDetailsToSelectOption(details) {
  const artist = details?.data?.artist;
  if (!artist?.id) {
    return null;
  }
  const images = (artist.images || []).filter(img => img && img.url);
  const image_big = images[0]?.url || null;
  const image_small =
    images.length === 1 ? image_big : images[images.length - 1]?.url || image_big;
  return {
    value: artist.id,
    label: artist.name,
    name: artist.name,
    image_big,
    image_small,
  };
}

export function findArtistBySpotifyId(spotifyId, rows) {
  return (rows || []).find(
    row => String(row.spotify_identifier) === String(spotifyId),
  );
}

export function findArtistByDeezerId(deezerId, rows) {
  return (rows || []).find(
    row => String(row.deezer_identifier) === String(deezerId),
  );
}

export function findArtistByAmazonId(amazonId, rows) {
  return (rows || []).find(
    row => String(row.amazon_music_identifier) === String(amazonId),
  );
}
